import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function GeneralInsurancePage() {
  const insuranceTypes = [
    {
      title: 'Travel Insurance',
      color: 'blue',
      icon: '✈️',
      description: 'Comprehensive protection for domestic and international travel',
      features: [
        'Medical emergencies abroad',
        'Trip cancellation/delay coverage',
        'Lost baggage protection',
        'Passport loss coverage',
        '24/7 emergency assistance',
        'COVID-19 coverage available'
      ],
      ideal: 'Essential for international travelers, students going abroad, or frequent business travelers'
    },
    {
      title: 'Motor Insurance',
      color: 'green',
      icon: '🚗',
      description: 'Complete protection for your car, bike, and commercial vehicles',
      features: [
        'Own damage cover',
        'Third-party liability (mandatory)',
        'Zero depreciation add-on',
        'Engine protection',
        'NCB (No Claim Bonus) protection',
        'Roadside assistance'
      ],
      ideal: 'Mandatory for all vehicle owners as per Motor Vehicles Act'
    },
    {
      title: 'Fire Insurance',
      color: 'red',
      icon: '🔥',
      description: 'Protect your property and business assets from fire-related damages',
      features: [
        'Fire and allied perils coverage',
        'Building and content protection',
        'Lightning damage',
        'Explosion coverage',
        'Impact damage from vehicles/aircraft',
        'Terrorism and malicious damage (optional)'
      ],
      ideal: 'Essential for home owners, shop owners, factories, and commercial establishments'
    },
    {
      title: 'Group Health Insurance',
      color: 'purple',
      icon: '👥',
      description: 'Comprehensive health coverage for employees and groups',
      features: [
        'Coverage for employees and their families',
        'Pre-existing disease coverage from day 1',
        'Maternity benefits',
        'Annual health check-ups',
        'Corporate wellness programs',
        'Lower premiums than individual plans'
      ],
      ideal: 'Perfect for employers, societies, and group associations'
    },
    {
      title: 'Home Insurance',
      color: 'orange',
      icon: '🏠',
      description: 'Complete protection for your home, belongings, and family',
      features: [
        'Building structure coverage',
        'Contents and valuables protection',
        'Burglary and theft coverage',
        'Natural calamity protection',
        'Public liability cover',
        'Temporary accommodation expenses'
      ],
      ideal: 'Recommended for all home owners and tenants wanting content protection'
    },
    {
      title: 'Group Personal Accident',
      color: 'purple',
      icon: '🩹',
      description: 'Comprehensive accident coverage for employees and group members',
      features: [
        'Accidental death and disability coverage',
        'Medical expenses due to accidents',
        'Permanent/temporary disability benefits',
        'Coverage for multiple employees',
        'No medical check-up required',
        '24/7 accident coverage worldwide'
      ],
      ideal: 'Essential for employers, corporate groups, societies, and associations'
    },
    {
      title: 'Workmen Compensation Insurance',
      color: 'orange',
      icon: '👷',
      description: 'Mandatory coverage for employers under Workmen Compensation Act',
      features: [
        'Coverage as per Workmen Compensation Act',
        'Death and disability compensation',
        'Medical expenses coverage',
        'Legal liability protection',
        'Covers contractual and casual workers',
        'Funeral expense reimbursement'
      ],
      ideal: 'Mandatory for all employers with workers/employees as per law'
    },
    {
      title: 'Cyber Liability Insurance',
      color: 'blue',
      icon: '🔐',
      description: 'Protection against cyber threats, data breaches, and digital risks',
      features: [
        'Data breach liability coverage',
        'Cyber extortion protection',
        'Business interruption coverage',
        'Legal defense costs',
        'Data recovery expenses',
        'Reputation management support'
      ],
      ideal: 'Critical for businesses handling customer data, IT companies, and online businesses'
    },
    {
      title: 'Commercial General Liability',
      color: 'green',
      icon: '🏢',
      description: 'Comprehensive liability protection for business operations and premises',
      features: [
        'Third-party bodily injury coverage',
        'Property damage liability',
        'Product liability protection',
        'Advertising injury coverage',
        'Legal defense costs',
        'Contractual liability coverage'
      ],
      ideal: 'Essential for all businesses, retail shops, offices, and commercial establishments'
    },
    {
      title: 'Professional Indemnity - Doctors/Hospitals',
      color: 'red',
      icon: '⚕️',
      description: 'Specialized coverage for medical professionals against malpractice claims',
      features: [
        'Medical negligence coverage',
        'Legal defense costs',
        'Compensation for errors/omissions',
        'Consent issue coverage',
        'Administrative error protection',
        'Court appearance expenses'
      ],
      ideal: 'Mandatory for doctors, hospitals, clinics, and healthcare professionals'
    },
    {
      title: 'Event Insurance',
      color: 'purple',
      icon: '🎉',
      description: 'Complete protection for events, exhibitions, weddings, and gatherings',
      features: [
        'Event cancellation coverage',
        'Public liability protection',
        'Vendor non-appearance cover',
        'Equipment and property damage',
        'Weather-related cancellation',
        'Accident and medical emergency coverage'
      ],
      ideal: 'Perfect for event organizers, wedding planners, exhibitions, and conferences'
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      hover: 'hover:border-blue-400',
      text: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-800'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      hover: 'hover:border-green-400',
      text: 'text-green-600',
      badge: 'bg-green-100 text-green-800'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      hover: 'hover:border-red-400',
      text: 'text-red-600',
      badge: 'bg-red-100 text-red-800'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      hover: 'hover:border-purple-400',
      text: 'text-purple-600',
      badge: 'bg-purple-100 text-purple-800'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      hover: 'hover:border-orange-400',
      text: 'text-orange-600',
      badge: 'bg-orange-100 text-orange-800'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-blue-600">General Insurance</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive protection for your assets, travel, vehicles, property, business, and liabilities
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What is General Insurance?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              General insurance covers all types of insurance except life insurance. It provides financial protection 
              for your assets, property, vehicles, and liabilities. Unlike life insurance, general insurance policies 
              are typically for a shorter duration (1-5 years) and are renewable.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                <div className="text-3xl mb-2">🛡️</div>
                <h3 className="font-bold text-gray-900 mb-1">Asset Protection</h3>
                <p className="text-sm text-gray-600">Safeguard your valuable assets from unforeseen events</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                <div className="text-3xl mb-2">💰</div>
                <h3 className="font-bold text-gray-900 mb-1">Financial Security</h3>
                <p className="text-sm text-gray-600">Avoid major financial losses due to accidents or damage</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                <div className="text-3xl mb-2">📋</div>
                <h3 className="font-bold text-gray-900 mb-1">Legal Compliance</h3>
                <p className="text-sm text-gray-600">Meet mandatory insurance requirements like motor insurance</p>
              </div>
            </div>
          </section>

          {/* Insurance Types */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our General Insurance Products</h2>
            <div className="space-y-8">
              {insuranceTypes.map((insurance, index) => {
                const colors = colorClasses[insurance.color as keyof typeof colorClasses];
                return (
                  <div 
                    key={index}
                    className={`${colors.bg} border-2 ${colors.border} ${colors.hover} rounded-2xl p-6 md:p-8 transition-all`}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-5xl">{insurance.icon}</div>
                      <div className="flex-1">
                        <h3 className={`text-2xl font-bold ${colors.text} mb-2`}>
                          {insurance.title}
                        </h3>
                        <p className="text-gray-700 text-lg">
                          {insurance.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Key Features:</h4>
                        <ul className="space-y-2">
                          {insurance.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Ideal For:</h4>
                        <div className={`${colors.badge} rounded-lg p-4`}>
                          <p className="text-sm leading-relaxed">
                            {insurance.ideal}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Choose Us for General Insurance?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                <h3 className="text-xl font-bold text-blue-600 mb-3">🏢 Multiple Insurance Partners</h3>
                <p className="text-gray-600">
                  We work with 10+ leading general insurance companies to find you the best coverage at competitive premiums.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                <h3 className="text-xl font-bold text-blue-600 mb-3">💡 Expert Guidance</h3>
                <p className="text-gray-600">
                  20+ years of experience helping clients choose the right coverage based on their specific needs and budget.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                <h3 className="text-xl font-bold text-blue-600 mb-3">🚀 Quick Claims Support</h3>
                <p className="text-gray-600">
                  We assist you throughout the claims process to ensure fast settlement and hassle-free experience.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                <h3 className="text-xl font-bold text-blue-600 mb-3">📞 Ongoing Support</h3>
                <p className="text-gray-600">
                  From policy purchase to renewals and claims, we're always available to help you with any insurance needs.
                </p>
              </div>
            </div>
          </section>

          {/* Licensed Advisor Badge */}
          <section className="mb-16">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-2xl p-8">
              <div className="flex items-start gap-6">
                <div className="bg-orange-600 rounded-xl p-4 flex-shrink-0">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Licensed General Insurance Advisor</h3>
                  <p className="text-gray-700 mb-4">
                    K Satish is officially licensed with ICICI Lombard General Insurance Co. Ltd, ensuring you receive 
                    professional and compliant general insurance advice for all your protection needs.
                  </p>
                  <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">Insurance Company:</p>
                        <p className="text-sm font-bold text-orange-800">ICICI Lombard General Insurance Co. Ltd</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">License Code:</p>
                        <p className="text-lg font-mono font-bold text-orange-700">ILG70251</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Get the Right Insurance Coverage Today
            </h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Don't leave your assets unprotected. Let us help you find the perfect insurance solution 
              tailored to your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#contact"
                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
              >
                Get Free Quote
              </a>
              <a
                href="https://wa.me/919248852187"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
              >
                WhatsApp Us
              </a>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
